/**
 * Contact Page - Olympic Fit Wellness
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ArrowRight, Send, CheckCircle } from 'lucide-react';
import api from '../lib/api';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: '',
    preferred_contact: 'email',
    preferred_location: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await api.submitContactForm(formData);
      setIsSubmitted(true);
    } catch (err) {
      setError('Failed to submit form. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const locations = [
    { 
      name: 'Lewisville (Main)', 
      address: '2601 E State Hwy 121 Building 1 Suite 114, Lewisville, TX 75056', 
      phone: '(469) 585-1023',
      hours: 'Mon-Thu: 6AM-8PM, Fri: 6AM-3PM, Sat: 8AM-3PM, Sun: 1-4PM'
    }
  ];

  if (isSubmitted) {
    return (
      <div className="bg-white min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-neutral-900 mb-4">
              Thank You!
            </h1>
            <p className="text-neutral-600 mb-8">
              We've received your message and will get back to you within 24 hours. In the meantime, feel free to explore our membership options or find a location near you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/membership" className="bg-black text-white font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-800 transition-colors">
                VIEW MEMBERSHIP
              </Link>
              <Link to="/locations" className="border border-neutral-300 text-neutral-700 font-semibold px-8 py-4 text-sm tracking-wider hover:border-black transition-colors">
                FIND LOCATION
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <span className="text-xs font-semibold tracking-widest text-[#3B82F6] mb-4 block">GET IN TOUCH</span>
          <h1 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out and let's start your wellness journey together.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-2xl font-heading font-bold text-neutral-900 mb-6">
                Send Us a Message
              </h2>
              
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded text-sm focus:outline-none focus:border-[#3B82F6]"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded text-sm focus:outline-none focus:border-[#3B82F6]"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-neutral-300 rounded text-sm focus:outline-none focus:border-[#3B82F6]"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Subject</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-neutral-300 rounded text-sm focus:outline-none focus:border-[#3B82F6]"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="membership">Membership Question</option>
                      <option value="tour">Schedule a Tour</option>
                      <option value="corporate">Corporate Wellness</option>
                      <option value="careers">Careers</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Preferred Location</label>
                  <select
                    name="preferred_location"
                    value={formData.preferred_location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-neutral-300 rounded text-sm focus:outline-none focus:border-[#3B82F6]"
                  >
                    <option value="">Select a location (optional)</option>
                    <option value="lewisville">Lewisville (Main)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-neutral-300 rounded text-sm focus:outline-none focus:border-[#3B82F6] resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-black text-white font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-800 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-heading font-bold text-neutral-900 mb-6">
                Contact Information
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Phone</h3>
                    <a href="tel:+14695851023" className="text-neutral-600 hover:text-[#3B82F6]">
                      (469) 585-1023
                    </a>
                    <p className="text-sm text-neutral-400">Mon-Fri: 6am-10pm, Sat-Sun: 7am-8pm</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Email</h3>
                    <a href="mailto:info@olympicfitwellness.com" className="text-neutral-600 hover:text-[#3B82F6]">
                      info@olympicfitwellness.com
                    </a>
                    <p className="text-sm text-neutral-400">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">Headquarters</h3>
                    <p className="text-neutral-600">
                      2601 E State Hwy 121<br />
                      Building 1 Suite 114<br />
                      Lewisville, TX 75056
                    </p>
                  </div>
                </div>
              </div>

              {/* Locations */}
              <h3 className="text-lg font-heading font-bold text-neutral-900 mb-4">
                Our Location
              </h3>
              <div className="space-y-4">
                {locations.map((loc, idx) => (
                  <div key={idx} className="p-4 bg-neutral-50 rounded-lg">
                    <h4 className="font-semibold text-neutral-900 mb-1">Olympic Fit Wellness - {loc.name}</h4>
                    <p className="text-sm text-neutral-600 mb-1">{loc.address}</p>
                    <a href={`tel:${loc.phone.replace(/[^0-9]/g, '')}`} className="text-sm text-[#3B82F6] block mb-2">{loc.phone}</a>
                    {loc.hours && <p className="text-xs text-neutral-500">{loc.hours}</p>}
                  </div>
                ))}
              </div>

              <Link 
                to="/locations" 
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:text-[#3B82F6] transition-colors"
              >
                VIEW ALL LOCATIONS <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-20 lg:py-28 bg-neutral-50">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-neutral-900 mb-4">
            Looking for Quick Answers?
          </h2>
          <p className="text-neutral-600 mb-8 max-w-xl mx-auto">
            Check out our FAQ for answers to common questions about membership, classes, and more.
          </p>
          <Link to="/faq" className="bg-black text-white font-semibold px-8 py-4 text-sm tracking-wider hover:bg-neutral-800 transition-colors inline-flex items-center gap-2">
            VIEW FAQ <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
